import React from 'react'
import { Box, Chip, Typography, useTheme } from '@mui/material'
import GenericButton from '../../../components/GenericButton'
import { ButtonType } from '../../../components/GenericButton/styles'
import { grey } from '../../../colors'
import { type PermissionCardForm, ReportAccess, ReportRoleType } from '../types'
import { cardShowsTaResults } from '../utils'

const REPORT_ACCESS_LABEL: Record<ReportAccess, string> = {
  [ReportAccess.FULL_ACCESS]: 'Full Access',
  [ReportAccess.VIEW_RESULTS_ONLY]: 'View Results Only',
  [ReportAccess.NO_ACCESS]: 'No Access'
}

const visibleQuestionTypesLabel = (card: PermissionCardForm): string =>
  card.questionTypes.length > 0 ? card.questionTypes.map((q) => q.label).join(', ') : 'None'

interface InheritedGroupCardProps {
  card: PermissionCardForm
  canEdit: boolean
  /** Lift this inherited group into an editable slot override (seeded from its values). */
  onOverride: () => void
}

const Field: React.FC<{ label: string, value: string }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', gap: 1 }}>
    <Typography fontSize="14px" color={grey[850]}>{label}:</Typography>
    <Typography fontSize="14px" color={grey[250]} fontWeight={500}>{value}</Typography>
  </Box>
)

/** Read-only display of an inherited Survey Project default group, with an Override action. */
const InheritedGroupCard: React.FC<InheritedGroupCardProps> = ({ card, canEdit, onOverride }) => {
  const theme = useTheme()
  const showAccessDetails = card.reportAccess !== ReportAccess.NO_ACCESS

  return (
    <Box
      sx={{
        border: `1px dashed ${grey[650]}`,
        borderRadius: theme.spacing(2),
        padding: theme.spacing(4),
        marginBottom: theme.spacing(3),
        backgroundColor: grey[100]
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {card.roles.map((r) => (
            <Chip key={r.roleId} label={r.roleName} size="small" />
          ))}
        </Box>
        {canEdit && (
          <GenericButton buttonType={ButtonType.Secondary} label="Override" onClick={onOverride} />
        )}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Field label="Report Access" value={REPORT_ACCESS_LABEL[card.reportAccess]} />
        {showAccessDetails && <Field label="Visible Question Types" value={visibleQuestionTypesLabel(card)} />}
        {card.roleType === ReportRoleType.COURSE_SECTION && cardShowsTaResults(card) && (
          <Field label="View TA Results" value={card.canViewTaResults ? 'Yes' : 'No'} />
        )}
      </Box>
    </Box>
  )
}

export default InheritedGroupCard
